import { useState } from "react";
import { pkBattles } from "@/mocks/home";

type Pick = { side: "left" | "right"; left: number; right: number } | undefined;

export default function PkArena() {
  const [picks, setPicks] = useState<Record<string, Pick>>({});

  const handlePick = (id: string, side: "left" | "right", baseLeft: number, baseRight: number) => {
    setPicks((prev) => {
      const current = prev[id];
      if (current?.side === side) return prev;
      const data = current ?? { side: undefined as unknown as "left" | "right", left: baseLeft, right: baseRight };
      const next = {
        side,
        left: data.left + (side === "left" ? 1 : 0) - (current?.side === "left" ? 1 : 0),
        right: data.right + (side === "right" ? 1 : 0) - (current?.side === "right" ? 1 : 0),
      };
      return { ...prev, [id]: next };
    });
  };

  return (
    <section id="pk" className="w-full scroll-mt-24 bg-foreground-950 py-14 md:py-20" aria-labelledby="pk-title">
      <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-accent-400">
            <i className="ri-sword-line text-sm" aria-hidden="true" />
            OPINION PK · 观点对决
          </span>
          <h2 id="pk-title" className="mt-3 font-heading text-3xl font-black tracking-tight text-background-50 md:text-4xl">
            观点 PK：你站哪一边？
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-background-200/75 md:text-base">
            同一个瓜，不同的人能吵出完全相反的结论。选一个立场，看看你和多少瓜友想的一样。
          </p>
        </div>

        <div className="mt-10 space-y-6">
          {pkBattles.map((battle) => {
            const pick = picks[battle.id];
            const left = pick?.left ?? battle.leftVotes;
            const right = pick?.right ?? battle.rightVotes;
            const total = left + right;
            const leftPercent = Math.round((left / total) * 100);

            return (
              <article key={battle.id} className="overflow-hidden rounded-lg border border-background-100/10 bg-background-50/5 p-5 md:p-7">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-bold text-accent-300">
                    <i className="ri-flashlight-fill text-xs" aria-hidden="true" />
                    {battle.tag}
                  </span>
                  <span className="text-xs text-background-200/60">
                    共 {total.toLocaleString()} 人参与
                  </span>
                </div>

                <h3 className="mt-4 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  {battle.question}
                </h3>

                <div className="mt-6 grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[1fr_auto_1fr] lg:gap-4">
                  <button
                    type="button"
                    onClick={() => handlePick(battle.id, "left", battle.leftVotes, battle.rightVotes)}
                    aria-pressed={pick?.side === "left"}
                    className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-colors ${
                      pick?.side === "left"
                        ? "border-primary-400 bg-primary-500/15"
                        : "border-background-100/15 hover:border-primary-400/70 hover:bg-primary-500/10"
                    }`}
                  >
                    <span className="flex w-full items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-bold text-background-50">
                        <i className="ri-checkbox-blank-circle-fill text-[8px] text-primary-400" aria-hidden="true" />
                        {battle.leftLabel}
                      </span>
                      <span className="font-heading text-lg font-black text-primary-300">{leftPercent}%</span>
                    </span>
                    <span className="text-xs leading-relaxed text-background-200/70">{battle.leftDesc}</span>
                    <span className="text-[11px] text-background-200/50">{left.toLocaleString()} 票</span>
                  </button>

                  <div className="flex items-center justify-center">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 font-heading text-sm font-black text-primary-950">
                      VS
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePick(battle.id, "right", battle.leftVotes, battle.rightVotes)}
                    aria-pressed={pick?.side === "right"}
                    className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-colors ${
                      pick?.side === "right"
                        ? "border-secondary-400 bg-secondary-500/15"
                        : "border-background-100/15 hover:border-secondary-400/70 hover:bg-secondary-500/10"
                    }`}
                  >
                    <span className="flex w-full items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-bold text-background-50">
                        <i className="ri-checkbox-blank-circle-fill text-[8px] text-secondary-400" aria-hidden="true" />
                        {battle.rightLabel}
                      </span>
                      <span className="font-heading text-lg font-black text-secondary-300">{100 - leftPercent}%</span>
                    </span>
                    <span className="text-xs leading-relaxed text-background-200/70">{battle.rightDesc}</span>
                    <span className="text-[11px] text-background-200/50">{right.toLocaleString()} 票</span>
                  </button>
                </div>

                <div className="mt-4 flex h-1.5 w-full overflow-hidden rounded-full bg-background-50/10">
                  <span className="h-full bg-primary-400" style={{ width: `${leftPercent}%` }} />
                  <span className="h-full bg-secondary-400" style={{ width: `${100 - leftPercent}%` }} />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}